import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logoPath from "@/assets/logo-houselet.png";

function buildQuoteTable(quote, pageWidth, marginX) {
  const head = [
    [
      "Cod.",
      "Descrizione",
      "Q.tà",
      "U.m.",
      "Prezzo unitario netto",
      "Iva",
      "Importo ivato",
    ],
  ];

  const lower = (v) => String(v || "").toLowerCase();

  const rows = [];
  const itemMap = new Map((quote.items || []).map((it) => [it.id, it]));

  const AREA_CATS_PDF = [
    "piastrelle",
    "rivestimenti parete",
    "parquet",
    "laminati",
    "decking esterno",
  ];

  (quote.items || []).forEach((it) => {
    const name = it.itemName || it.subBrand || it.brand || "-";
    const isTiles = lower(it.category) === "piastrelle";
    const isArea = AREA_CATS_PDF.includes(lower(it.category));
    const isBattiscopa = lower(name).includes("battiscopa");

    const rawQty =
      it?.QuoteItem?.quantity ?? (isTiles ? it?.boxQuantity : it?.pieces) ?? 0;
    const baseQty = Number(rawQty);
    const sqmPerBox = Number(it.sqmPerBox || 0) || 1;
    const qty = isTiles ? Number((baseQty * sqmPerBox).toFixed(2)) : baseQty;
    const unit = isBattiscopa ? "mL" : isTiles ? "mQ" : "pz";

    const unitPrice = Number(it?.QuoteItem?.price ?? it.price ?? 0);
    // IVA della RIGA preventivo (through), poi fallback su articolo, poi 22.
    // Sempre ?? perché 0 (IVA 0%) è un valore valido.
    const iva = it?.QuoteItem?.iva ?? it?.iva ?? 22;
    const lineTotal = Number((unitPrice * qty).toFixed(2));
    // Prezzo unitario netto (senza IVA) solo per la visualizzazione PDF.
    // IVA 0% preservata: ivaRate 0 → divisione per 1 → netto = ivato.
    const ivaRate = Number(iva ?? 0);
    const unitNetPrice = Number((unitPrice / (1 + ivaRate / 100)).toFixed(2));

    const dim =
      it.formatLabel ||
      (it.widthCm && it.heightCm ? `${it.widthCm}×${it.heightCm}` : null);
    const normStr = (s) => (s || "").replace(/×/g, "x").toLowerCase();
    const thickStr = it.thicknessMm ? `${parseFloat(it.thicknessMm)}mm` : null;
    const specParts = isArea
      ? [
          dim && !normStr(name).includes(normStr(dim)) ? dim : null,
          it.surface,
          thickStr && !name.toLowerCase().includes(thickStr) ? thickStr : null,
        ].filter(Boolean)
      : [];
    const desc = specParts.length
      ? `${name} ${specParts.join(" ")}`.trim()
      : name;

    rows.push({
      group: String(it.category || "Generale")
        .trim()
        .toUpperCase(),
      code: it.itemCode || it.code || "-",
      desc,
      qty,
      unit,
      unitPrice,
      unitNetPrice,
      iva,
      lineTotal,
    });
  });

  (quote.quote_items || []).forEach((qi) => {
    const id = qi?.item?.id;
    if (id && itemMap.has(id)) return;

    const it2 = qi?.item || {};
    const name = it2.itemName || "-";

    const isTiles = lower(it2.category) === "piastrelle";
    const isArea2 = AREA_CATS_PDF.includes(lower(it2.category));
    const isBattiscopa = lower(name).includes("battiscopa");

    const qty = Number(qi.quantity || 0);
    const unit = isBattiscopa ? "mL" : isTiles ? "mQ" : "pz";

    const unitPrice = Number(qi.price || 0);
    const iva = qi?.iva ?? 22;
    const lineTotal = Number((unitPrice * qty).toFixed(2));
    // Prezzo unitario netto (senza IVA) solo per la visualizzazione PDF.
    // IVA 0% preservata: ivaRate 0 → divisione per 1 → netto = ivato.
    const ivaRate = Number(iva ?? 0);
    const unitNetPrice = Number((unitPrice / (1 + ivaRate / 100)).toFixed(2));

    const group = String(it2.category || "Generale")
      .trim()
      .toUpperCase();

    const dim2 =
      it2.formatLabel ||
      (it2.widthCm && it2.heightCm ? `${it2.widthCm}×${it2.heightCm}` : null);
    const normStr2 = (s) => (s || "").replace(/×/g, "x").toLowerCase();
    const thickStr2 = it2.thicknessMm
      ? `${parseFloat(it2.thicknessMm)}mm`
      : null;
    const specParts2 = isArea2
      ? [
          dim2 && !normStr2(name).includes(normStr2(dim2)) ? dim2 : null,
          it2.surface,
          thickStr2 && !name.toLowerCase().includes(thickStr2)
            ? thickStr2
            : null,
        ].filter(Boolean)
      : [];
    const desc2 = specParts2.length
      ? `${name} ${specParts2.join(" ")}`.trim()
      : name;

    rows.push({
      group,
      code: it2.itemCode || it2.code || "-",
      desc: desc2,
      qty,
      unit,
      unitPrice,
      unitNetPrice,
      iva,
      lineTotal,
    });
  });

  const order = [];
  const groups = {};
  rows.forEach((r) => {
    if (!groups[r.group]) {
      groups[r.group] = [];
      order.push(r.group);
    }
    groups[r.group].push(r);
  });

  const body = [];
  order.forEach((g) => {
    body.push([
      {
        content: g,
        colSpan: 7,
        styles: {
          fillColor: [235, 235, 235],
          fontStyle: "bold",
          halign: "left",
        },
      },
    ]);
    groups[g].forEach((r) => {
      body.push([
        r.code,
        r.desc,
        String(r.qty),
        r.unit,
        `€ ${r.unitNetPrice.toFixed(2)}`,
        `${r.iva}%`,
        `€ ${r.lineTotal.toFixed(2)}`,
      ]);
    });
  });

  const usable = pageWidth - 2 * marginX;
  const w = {
    cod: 0.1,
    qty: 0.1,
    um: 0.08,
    price: 0.16,
    iva: 0.08,
    tot: 0.18,
  };
  const cw = {
    0: Math.floor(usable * w.cod),
    2: Math.floor(usable * w.qty),
    3: Math.floor(usable * w.um),
    4: Math.floor(usable * w.price),
    5: Math.floor(usable * w.iva),
    6: Math.floor(usable * w.tot),
  };

  return {
    head,
    body,
    tableWidth: usable,
    margin: { left: marginX, right: marginX },
    columnStyles: {
      0: { cellWidth: cw[0], halign: "left" },
      1: { cellWidth: "auto", minCellWidth: 40, halign: "left" },
      2: { cellWidth: cw[2], halign: "right" },
      3: { cellWidth: cw[3], halign: "center" },
      4: { cellWidth: cw[4], halign: "right" },
      5: { cellWidth: cw[5], halign: "right" },
      6: { cellWidth: cw[6], halign: "right" },
    },
  };
}

/**
 * @param {object} params
 * @param {object} params.quote         - dati preventivo/ordine completi
 * @param {object} params.toast         - istanza vue-toastification ($toast)
 * @param {Function} params.uploadQuoteDocument - fn(payload) che esegue il dispatch Vuex
 * @param {Function} [params.onComplete]        - callback dopo upload riuscito
 */
export async function generateQuotePdf({
  quote,
  toast,
  uploadQuoteDocument,
  onComplete,
  isOrder = false,
  filenameOverride = null,
}) {
  try {
    const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 10;
    const marginY = 10;
    let y = marginY;

    if (logoPath) {
      try {
        doc.addImage(logoPath, "PNG", marginX - 5, y, 60, 24);
        // eslint-disable-next-line no-empty
      } catch {}
    }
    doc.setFontSize(9).setFont("helvetica", "normal");
    [
      "HOUSELET",
      "Show-room",
      "Via Salvatore Averna, 4",
      "95045 - Misterbianco (CT)",
      "T.095 8164002 - 3316557313",
    ].forEach((text, i) => {
      const x = pageWidth - marginX - doc.getTextWidth(text);
      doc.text(text, x, y + 5 + i * 4);
    });
    y += 48;

    const printLabelValue = (
      label,
      value,
      xPos,
      yPos,
      maxWidth = null,
      link = null
    ) => {
      const labelCap = label.charAt(0).toUpperCase() + label.slice(1);
      doc.setFont("helvetica", "bold");
      doc.text(`${labelCap}:`, xPos, yPos);
      const off = doc.getTextWidth(`${labelCap}: `) + 0.8;
      doc.setFont("helvetica", "normal");
      const val = value || "N/D";
      let display = val;
      if (maxWidth) {
        while (
          doc.getTextWidth(display) > maxWidth - off &&
          display.length > 3
        ) {
          display = display.slice(0, -4) + "…";
        }
      }
      if (link) {
        doc.setTextColor(0, 0, 255);
        doc.textWithLink(display, xPos + off, yPos, { url: link });
        doc.setTextColor(0);
      } else {
        doc.text(display, xPos + off, yPos);
      }
      return yPos + 4;
    };

    const contactValue = quote?.Client?.pec || quote?.Client?.email || "";
    const contactLabel = quote?.Client?.pec ? "pec" : "email";
    const lh = (() => {
      const raw = quote?.Client?.legalHeadquarters;
      if (!raw) return {};
      if (typeof raw === "object") return raw;
      try {
        return JSON.parse(raw);
      } catch {
        return {};
      }
    })();
    const rightX = pageWidth / 2 - 10;
    const colW = pageWidth - rightX - marginX;
    const leftColW = rightX - marginX - 10;
    const formattedDate = new Date(quote.date).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    // Ultimo pagamento usato per generare il PDF. Calcolato qui (prima del
    // blocco "Dettaglio ordine") perché serve sia per la data documento sia,
    // più in basso, per la riga di riepilogo e i dettagli pagamento.
    const lastPayment = [...(quote.payments || [])].sort((a, b) => {
      const da = new Date(a?.paidAt ?? a?.createdAt ?? 0).getTime();
      const dbb = new Date(b?.paidAt ?? b?.createdAt ?? 0).getTime();
      if (da !== dbb) return da - dbb;
      return (Number(a?.id) || 0) - (Number(b?.id) || 0);
    })[(quote.payments || []).length - 1];

    const formattedDocDate = lastPayment
      ? new Date(
          lastPayment.paidAt ?? lastPayment.createdAt ?? Date.now()
        ).toLocaleDateString("it-IT", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : null;

    // LEFT: Dettaglio cliente
    let leftX = marginX;
    doc
      .setFontSize(12)
      .setFont("helvetica", "bold")
      .text("Dettaglio cliente", leftX, y);
    let yLeft = y + 8;
    doc.setFontSize(9);
    yLeft = printLabelValue("cliente", quote?.Client?.clientName, leftX, yLeft);
    yLeft = printLabelValue(
      "telefono",
      quote?.Client?.phoneNumber,
      leftX,
      yLeft
    );
    yLeft = printLabelValue(contactLabel, contactValue, leftX, yLeft);
    yLeft = printLabelValue(
      "partita iva",
      quote?.Client?.vatNumber,
      leftX,
      yLeft
    );
    yLeft = printLabelValue(
      "codice fiscale",
      quote?.Client?.taxCode,
      leftX,
      yLeft
    );
    yLeft = printLabelValue(
      "azienda",
      quote?.Client?.companyName,
      leftX,
      yLeft
    );
    yLeft = printLabelValue("sede legale", lh.address, leftX, yLeft, leftColW);

    // RIGHT: Dettaglio preventivo
    doc
      .setFontSize(12)
      .setFont("helvetica", "bold")
      .text(isOrder ? "Dettaglio ordine" : "Dettaglio preventivo", rightX, y);
    let yRight = y + 8;
    doc.setFontSize(9);
    if (lastPayment) {
      yRight = printLabelValue("data ordine", formattedDate, rightX, yRight);
      yRight = printLabelValue(
        "data documento",
        formattedDocDate,
        rightX,
        yRight
      );
    } else {
      yRight = printLabelValue("data", formattedDate, rightX, yRight);
    }
    yRight = printLabelValue(
      "indirizzo di consegna",
      quote?.location?.address,
      rightX,
      yRight,
      colW,
      quote?.location?.link || null
    );

    y = Math.max(yLeft, yRight) + 4;
    doc.setDrawColor(150).line(marginX, y, pageWidth - marginX, y);
    y += 6;

    const tbl = buildQuoteTable(quote, pageWidth, marginX);
    autoTable(doc, {
      startY: y,
      theme: "grid",
      styles: { fontSize: 10, cellPadding: 1.5 },
      headStyles: { fillColor: [200, 200, 200], fontStyle: "bold" },
      head: tbl.head,
      body: tbl.body,
      columnStyles: tbl.columnStyles,
      tableWidth: tbl.tableWidth,
      margin: tbl.margin,
    });
    y = (doc.lastAutoTable?.finalY || y) + 10;

    const PACKAGING_COST = 30;
    const hasSurcharge = Number(quote.surcharge || 0) > 0;
    const itemsTotal = hasSurcharge
      ? Number(quote.realTotalPrice || quote.totalPrice || 0)
      : Number(quote.totalPrice || 0);
    const base = itemsTotal + PACKAGING_COST;
    const percent = Number(quote.discount_percent || 0);
    const discountFromCol = Number(quote.discount || 0);
    const discountCalc = Number(((base * percent) / 100).toFixed(2));
    const discount = discountFromCol > 0 ? discountFromCol : discountCalc;
    const afterDiscount = Math.max(0, Number((base - discount).toFixed(2)));
    const deposit = Number(quote.deposit || 0);
    const toPay = Math.max(0, Number((afterDiscount - deposit).toFixed(2)));

    // Label + importo della riga pagamento basati sull'ultimo pagamento
    // registrato. "Totale pagato" mostra invece il cumulativo (deposit).
    const PAYMENT_TYPE_LABELS = {
      acconto: "Acconto",
      saldo_parziale: "Saldo parziale",
      saldo_finale: "Saldo finale",
    };
    const depositLabel = PAYMENT_TYPE_LABELS[lastPayment?.type] || "Acconto";
    const lastPaymentAmount = Number(lastPayment?.amount || 0);

    const minVisualRoom = 55;
    if (pageHeight - y < minVisualRoom) {
      doc.addPage();
      y = marginY;
    }

    const headPadTop = 10;
    const lineH = 6;

    const leftTitle = "Riepilogo";
    const leftLines = [
      `Totale ordine: € ${itemsTotal.toFixed(2)}`,
      `Spese imballo: € ${PACKAGING_COST.toFixed(2)}`,
    ];
    if (discount > 0) {
      const pctLabel = percent ? ` (${percent}%)` : "";
      leftLines.push(`Sconto${pctLabel}: € ${discount.toFixed(2)}`);
    }
    // Riga ultimo pagamento (label dinamica + importo dell'ultimo pagamento)
    if (deposit > 0 && lastPaymentAmount > 0) {
      leftLines.push(`${depositLabel}: € ${lastPaymentAmount.toFixed(2)}`);
    }
    // Totale cumulativo pagato (vecchio valore deposit)
    if (deposit > 0) {
      leftLines.push(`Totale pagato: € ${deposit.toFixed(2)}`);
    }
    if (discount > 0 || deposit > 0) {
      leftLines.push({
        text: `Totale da pagare: € ${toPay.toFixed(2)}`,
        bold: true,
      });
    } else {
      leftLines.push({ text: `Totale: € ${base.toFixed(2)}`, bold: true });
    }

    const PAYMENT_METHOD_LABELS = {
      contanti: "Contanti",
      bonifico: "Bonifico",
      "carta di credito": "Carta di credito",
    };
    const centerTitle = "Dettagli pagamento";
    const centerLines = ["IBAN: IT00000000000000", "Intestato a: HOUSELET"];
    // Metodo pagamento mostrato solo se presente sull'ultimo pagamento.
    if (lastPayment?.method) {
      const methodKey = String(lastPayment.method || "").toLowerCase();
      const methodLabel =
        PAYMENT_METHOD_LABELS[methodKey] || lastPayment.method;
      centerLines.unshift(`Metodo doc. corrente: ${methodLabel}`);
    }

    const rightTitle = "Firma";
    const sigLineYOffset = 12;

    const leftHeight = headPadTop + 2 + 7 + leftLines.length * lineH + 8;
    const centerHeight = headPadTop + 2 + 7 + centerLines.length * 8 + 8;
    const rightHeight = headPadTop + 2 + 7 + sigLineYOffset + 8;
    const minBlockH = 40;
    const blockH = Math.max(minBlockH, leftHeight, centerHeight, rightHeight);

    doc.setFillColor(240, 240, 240);
    doc.rect(marginX, y, pageWidth - 2 * marginX, blockH, "F");

    const colW3 = (pageWidth - 2 * marginX) / 3;
    const colPad = 4; // padding interno colonna (mm)
    const col1X = marginX + colPad; // bordo sinistro colonna 1 (Riepilogo)
    const col2X = marginX + colW3 + colPad; // bordo sinistro colonna 2 (Dettagli pagamento)
    const col3X = marginX + colW3 * 2.5; // Firma: invariata (centrata)

    // Colonna 1 — Riepilogo (allineata a sinistra)
    let fyLeft = y + headPadTop;
    doc.setFont("helvetica", "bold").setFontSize(12);
    doc.text(leftTitle, col1X, fyLeft);
    fyLeft += 7;
    doc.setFont("helvetica", "normal").setFontSize(10);
    leftLines.forEach((ln) => {
      if (typeof ln === "string") {
        doc.text(ln, col1X, fyLeft);
      } else if (ln && ln.bold) {
        doc.setFont("helvetica", "bold");
        doc.text(ln.text, col1X, fyLeft);
        doc.setFont("helvetica", "normal");
      }
      fyLeft += lineH;
    });

    // Colonna 2 — Dettagli pagamento (allineata a sinistra)
    let fyCenter = y + headPadTop;
    doc.setFont("helvetica", "bold").setFontSize(12);
    doc.text(centerTitle, col2X, fyCenter);
    fyCenter += 7;
    doc.setFont("helvetica", "normal").setFontSize(10);
    centerLines.forEach((ln, i) => {
      doc.text(ln, col2X, fyCenter + i * 8);
    });

    // Colonna 3 — Firma (centrata, linea firma sotto il titolo)
    const sigWidth = colW3 * 0.6;
    const sigStart = col3X - sigWidth / 2;
    const sigEnd = col3X + sigWidth / 2;
    let fyRight = y + headPadTop;
    doc.setFont("helvetica", "bold").setFontSize(12);
    doc.text(rightTitle, col3X, fyRight, { align: "center" });
    fyRight += sigLineYOffset;
    doc.setDrawColor(0);
    doc.line(sigStart, fyRight, sigEnd, fyRight);

    y += blockH + 5;

    // ---- NOTE DEL PREVENTIVO ----
    if (quote.technical_queries) {
      const noteText = String(quote.technical_queries).trim();
      if (noteText) {
        doc.setFont("helvetica", "bold").setFontSize(9);
        doc.text("Note:", marginX, y);
        y += 5;
        doc.setFont("helvetica", "normal").setFontSize(8);
        const wrappedNote = doc.splitTextToSize(
          noteText,
          pageWidth - 2 * marginX
        );
        wrappedNote.forEach((ln) => {
          if (pageHeight - y < 10) {
            doc.addPage();
            y = marginY;
          }
          doc.text(ln, marginX, y);
          y += 4.5;
        });
        y += 4;
      }
    }

    // ---- CONDIZIONI DI PAGAMENTO ----
    const condizioni = [
      "1) Termini di pagamento: 40% anticipato all'ordine, saldo ad avviso merce pronta ns. deposito.",
      "2) Condizioni generali di vendita: L'autorizzazione al reso, o alla sostituzione, per quanto non conforme",
      "   all'ordine, deve essere preventivamente autorizzata dal produttore.",
      "3) Spese di deposito: Nel caso di mancato pagamento e/o ritardo nel ritiro e/o ingiustificato rifiuto della",
      "   merce da parte del cliente il venditore si riserva il diritto di addebitare al cliente i costi di deposito",
      "   della merce ex art. 1781 c.c. in misura di 30€ per ogni giorno di ritardo dalla data di presunta consegna.",
    ];
    const condLineH = 4.5;
    const condBlockH = 6 + condizioni.length * condLineH + 4;
    if (pageHeight - y < condBlockH) {
      doc.addPage();
      y = marginY;
    }
    doc.setTextColor(150).setFont("helvetica", "bold").setFontSize(9);
    doc.text("Condizioni di pagamento:", marginX, y);
    y += 5;
    doc.setFont("helvetica", "normal").setFontSize(8);
    condizioni.forEach((ln) => {
      doc.text(ln, marginX, y);
      y += condLineH;
    });
    doc.setTextColor(0);
    y += 4;

    const filenameBase = `${isOrder ? "Ordine" : "Preventivo"}_N_${
      quote.id
    }.pdf`;
    const isDev =
      process.env.NODE_ENV === "development" ||
      import.meta?.env?.MODE === "development" ||
      process.env?.VUE_APP_NODE_ENV === "development";

    const withSuffixBeforeExt = (filename, suffix) => {
      if (!suffix) return filename;
      const dot = filename.lastIndexOf(".");
      if (dot <= 0) return `${filename}.${suffix}`;
      return `${filename.slice(0, dot)}.${suffix}${filename.slice(dot)}`;
    };

    // Override esplicito (es. PDF pagamento "tipologia_YYYY-MM-DD_HH-mm.pdf"):
    // usato così com'è, senza suffisso ".dev". Senza override resta il
    // comportamento esistente del documento principale.
    const finalName = filenameOverride
      ? filenameOverride
      : withSuffixBeforeExt(filenameBase, isDev ? "dev" : "");
    const blob = doc.output("blob");
    const file = new File([blob], finalName, { type: "application/pdf" });

    try {
      await uploadQuoteDocument({
        quoteId: quote.id,
        document: file,
        filename: finalName,
      });
      toast.success("PDF generato e caricato con successo!");
      doc.save(finalName);
    } catch (error) {
      console.error("Errore durante l'upload del PDF:", error);
      toast.error(
        error.response?.data?.details ||
          error.response?.data?.error ||
          "Errore durante il caricamento del PDF."
      );
      return;
    }

    if (onComplete) await onComplete();
  } catch (error) {
    toast.error(
      error?.response?.data?.details ||
        error?.response?.data?.error ||
        "Errore durante la generazione/caricamento del PDF."
    );
  }
}
